import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api';
import type { ProjectChapter, ProjectDetail, ProjectSummary } from '../lib/api';
import { CardTitle, Icon, Spinner, StatusBadge, Waveform } from '../components/ui';

interface Voice {
  id: string;
  name: string;
  provider: string;
  language: string;
}

const POLL_MS = 4000;

function formatDate(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds)) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Audiobooks() {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected project (detail view)
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Voices for the create modal
  const [voices, setVoices] = useState<Voice[]>([]);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVoice, setNewVoice] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState('');

  // Add-chapter form
  const [chapterTitle, setChapterTitle] = useState('');
  const [chapterText, setChapterText] = useState('');
  const [chapterBusy, setChapterBusy] = useState(false);
  const [chapterError, setChapterError] = useState('');

  // Chapter editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [chapterGenBusy, setChapterGenBusy] = useState<string | null>(null);
  const [regenConfirm, setRegenConfirm] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingStitched, setDownloadingStitched] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Book details modal (name/description + MP3 metadata)
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [metaName, setMetaName] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaAuthor, setMetaAuthor] = useState('');
  const [metaNarrator, setMetaNarrator] = useState('');
  const [metaGenre, setMetaGenre] = useState('');
  const [metaYear, setMetaYear] = useState('');
  const [enhancingId, setEnhancingId] = useState<string | null>(null);
  const [clearAudioConfirm, setClearAudioConfirm] = useState<string | null>(null);
  const [clearingAudioId, setClearingAudioId] = useState<string | null>(null);

  // Actions
  const [generateBusy, setGenerateBusy] = useState(false);
  const [stitchBusy, setStitchBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [deleteProjectConfirm, setDeleteProjectConfirm] = useState(false);

  // Chapter playback (one at a time)
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadProjects = useCallback(async () => {
    const res = await api.getProjects();
    if (res.success && res.data) {
      setProjects(res.data);
    } else if (res.error) {
      setError(res.error.message);
    }
    setLoading(false);
  }, []);

  const loadDetail = useCallback(async (id: string, silent = false) => {
    if (!silent) setDetailLoading(true);
    const res = await api.getProject(id);
    if (res.success && res.data) {
      setDetail(res.data);
    } else if (!silent) {
      setActionError(res.error?.message || 'Failed to load audiobook');
    }
    if (!silent) setDetailLoading(false);
  }, []);

  useEffect(() => {
    loadProjects();
    api.getVoices().then((res) => {
      if (res.success && res.data) setVoices(res.data);
    });
  }, [loadProjects]);

  // Poll the open project while any chapter is queued/processing or a stitch runs.
  const busy =
    !!detail &&
    (detail.chapters.some((ch) => ch.status === 'queued' || ch.status === 'processing') ||
      detail.stitched_status === 'stitching');

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (selectedId && busy) {
      pollRef.current = setInterval(() => loadDetail(selectedId, true), POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [selectedId, busy, loadDetail]);

  const openProject = (id: string) => {
    setSelectedId(id);
    setDetail(null);
    setActionError('');
    setChapterError('');
    setEditingId(null);
    setDeleteConfirm(null);
    setDeleteProjectConfirm(false);
    loadDetail(id);
  };

  const backToList = () => {
    setSelectedId(null);
    setDetail(null);
    setPlayingId(null);
    loadProjects();
  };

  const submitCreate = async () => {
    if (createBusy) return;
    setCreateError('');
    if (!newName.trim()) {
      setCreateError('Give your audiobook a name.');
      return;
    }
    setCreateBusy(true);
    const voice = voices.find((v) => `${v.provider}:${v.id}` === newVoice);
    const res = await api.createProject({
      name: newName.trim(),
      description: newDescription.trim() || undefined,
      voice_id: voice?.id,
      provider: voice?.provider,
      language: newLanguage || voice?.language || undefined,
    });
    setCreateBusy(false);
    if (res.success && res.data) {
      setCreateOpen(false);
      setNewName('');
      setNewDescription('');
      setNewVoice('');
      setNewLanguage('');
      await loadProjects();
      openProject(res.data.id);
    } else {
      setCreateError(res.error?.message || 'Failed to create audiobook');
    }
  };

  const submitChapter = async () => {
    if (!detail || chapterBusy) return;
    setChapterError('');
    if (!chapterTitle.trim() || !chapterText.trim()) {
      setChapterError('Chapter title and text are both required.');
      return;
    }
    setChapterBusy(true);
    const res = await api.addChapter(detail.id, { title: chapterTitle.trim(), text: chapterText });
    setChapterBusy(false);
    if (res.success) {
      setChapterTitle('');
      setChapterText('');
      loadDetail(detail.id, true);
    } else {
      setChapterError(res.error?.message || 'Failed to add chapter');
    }
  };

  const startEdit = (chapter: ProjectChapter) => {
    setEditingId(chapter.id);
    setEditTitle(chapter.title);
    setEditText(chapter.text_content);
    setActionError('');
  };

  const submitEdit = async () => {
    if (!detail || !editingId || editBusy) return;
    setEditBusy(true);
    const original = detail.chapters.find((ch) => ch.id === editingId);
    const payload: { title?: string; text?: string } = {};
    if (editTitle.trim() && editTitle.trim() !== original?.title) payload.title = editTitle.trim();
    if (editText.trim() && editText !== original?.text_content) payload.text = editText;
    if (Object.keys(payload).length === 0) {
      setEditBusy(false);
      setEditingId(null);
      return;
    }
    const res = await api.updateChapter(detail.id, editingId, payload);
    setEditBusy(false);
    if (res.success) {
      setEditingId(null);
      loadDetail(detail.id, true);
    } else {
      setActionError(res.error?.message || 'Failed to update chapter');
    }
  };

  const clearChapterAudio = async (chapterId: string) => {
    if (!detail || clearingAudioId) return;
    setClearAudioConfirm(null);
    setClearingAudioId(chapterId);
    const res = await api.deleteChapterAudio(detail.id, chapterId);
    setClearingAudioId(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to clear the chapter audio');
    }
    loadDetail(detail.id, true);
  };

  const enhanceOneChapter = async (chapterId: string) => {
    if (!detail || enhancingId) return;
    setActionError('');
    setEnhancingId(chapterId);
    const res = await api.enhanceChapter(detail.id, chapterId);
    setEnhancingId(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to enhance the chapter');
    }
    loadDetail(detail.id, true);
  };

  const generateOneChapter = async (chapterId: string) => {
    if (!detail || chapterGenBusy) return;
    setRegenConfirm(null);
    setChapterGenBusy(chapterId);
    const res = await api.generateChapter(detail.id, chapterId);
    setChapterGenBusy(null);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to start chapter generation');
    }
    loadDetail(detail.id, true);
  };

  const downloadAllChapters = async () => {
    if (!detail || downloadingAll) return;
    const completed = [...detail.chapters]
      .sort((a, b) => a.idx - b.idx)
      .filter((ch) => ch.status === 'completed' && ch.audio_file_id);
    if (completed.length === 0) return;

    setDownloadingAll(true);
    for (const ch of completed) {
      const blob = await api.downloadAudioBlob(ch.audio_file_id!);
      if (!blob) {
        setActionError(`Failed to download "${ch.title}" — try again`);
        continue;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${String(ch.idx).padStart(2, '0')} - ${ch.title.replace(/[^\w\-. ]+/g, '_').slice(0, 100)}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
    setDownloadingAll(false);
  };

  const openDetails = () => {
    if (!detail) return;
    setMetaName(detail.name);
    setMetaDescription(detail.description || '');
    setMetaAuthor(detail.author || '');
    setMetaNarrator(detail.narrator || '');
    setMetaGenre(detail.genre || '');
    setMetaYear(detail.publish_year || '');
    setDetailsError('');
    setDetailsOpen(true);
  };

  const submitDetails = async () => {
    if (!detail || detailsBusy) return;
    if (!metaName.trim()) {
      setDetailsError('The audiobook needs a name.');
      return;
    }
    if (metaYear.trim() && !/^\d{4}$/.test(metaYear.trim())) {
      setDetailsError('Year must be 4 digits (e.g. 2026).');
      return;
    }
    setDetailsBusy(true);
    const res = await api.updateProject(detail.id, {
      name: metaName.trim(),
      description: metaDescription.trim() || null,
      author: metaAuthor.trim() || null,
      narrator: metaNarrator.trim() || null,
      genre: metaGenre.trim() || null,
      publish_year: metaYear.trim() || null,
    });
    setDetailsBusy(false);
    if (res.success) {
      setDetailsOpen(false);
      loadDetail(detail.id, true);
    } else {
      setDetailsError(res.error?.message || 'Failed to save the book details');
    }
  };

  // Copies the public listening link, minting the share token on first use.
  const copyShareLink = async () => {
    if (!detail || shareBusy) return;
    setActionError('');
    setShareBusy(true);
    let token = detail.share_token || null;
    if (!token) {
      const res = await api.shareProject(detail.id);
      if (res.success && res.data) {
        token = res.data.share_token;
      } else {
        setActionError(res.error?.message || 'Failed to create the share link');
      }
    }
    if (token) {
      // Path form (not hash) so chat apps can build a preview with the book name.
      // The ?v= stamp changes whenever the book changes, so re-copied links look
      // new to chat apps and get a fresh preview instead of a cached one.
      const stamp = detail.updated_at.replace(/\D/g, '').slice(0, 12);
      const url = `${window.location.origin}/listen/${token}?v=${stamp}`;
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2500);
      } catch {
        window.prompt('Copy the listening link:', url);
      }
      loadDetail(detail.id, true);
    }
    setShareBusy(false);
  };

  const revokeShareLink = async () => {
    if (!detail || shareBusy) return;
    setShareBusy(true);
    const res = await api.unshareProject(detail.id);
    setShareBusy(false);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to disable the share link');
    }
    loadDetail(detail.id, true);
  };

  // Download through the API (not the raw R2 URL) so the MP3 gets ID3 metadata
  // and a clean filename.
  const downloadStitched = async () => {
    if (!detail?.stitched_file || downloadingStitched) return;
    setDownloadingStitched(true);
    const blob = await api.downloadAudioBlob(detail.stitched_file.id);
    setDownloadingStitched(false);
    if (!blob) {
      setActionError('Failed to download the audiobook — try again');
      return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${detail.name.replace(/[^\w\-. ]+/g, '_').slice(0, 100)}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const moveChapter = async (chapterId: string, dir: -1 | 1) => {
    if (!detail || reordering) return;
    const ordered = [...detail.chapters].sort((a, b) => a.idx - b.idx);
    const from = ordered.findIndex((ch) => ch.id === chapterId);
    const to = from + dir;
    if (from < 0 || to < 0 || to >= ordered.length) return;

    [ordered[from], ordered[to]] = [ordered[to], ordered[from]];

    // Optimistic update so the row moves immediately
    setDetail({
      ...detail,
      chapters: ordered.map((ch, i) => ({ ...ch, idx: i + 1 })),
    });

    setReordering(true);
    const res = await api.reorderChapters(detail.id, ordered.map((ch) => ch.id));
    setReordering(false);
    if (!res.success) {
      setActionError(res.error?.message || 'Failed to reorder chapters');
    }
    loadDetail(detail.id, true); // re-sync (also picks up stitched-status invalidation)
  };

  const removeChapter = async (chapterId: string) => {
    if (!detail) return;
    const res = await api.deleteChapter(detail.id, chapterId);
    setDeleteConfirm(null);
    if (res.success) {
      loadDetail(detail.id, true);
    } else {
      setActionError(res.error?.message || 'Failed to delete chapter');
    }
  };

  const generateAll = async () => {
    if (!detail || generateBusy) return;
    setActionError('');
    setGenerateBusy(true);
    const res = await api.generateProject(detail.id);
    setGenerateBusy(false);
    if (res.success) {
      loadDetail(detail.id, true);
    } else {
      setActionError(res.error?.message || 'Failed to start generation');
    }
  };

  const stitch = async () => {
    if (!detail || stitchBusy) return;
    setActionError('');
    setStitchBusy(true);
    const res = await api.stitchProject(detail.id);
    setStitchBusy(false);
    if (res.success) {
      loadDetail(detail.id, true);
    } else {
      setActionError(res.error?.message || 'Failed to start stitching');
    }
  };

  const removeProject = async () => {
    if (!detail) return;
    const res = await api.deleteProject(detail.id);
    if (res.success) {
      backToList();
    } else {
      setActionError(res.error?.message || 'Failed to delete audiobook');
      setDeleteProjectConfirm(false);
    }
  };

  const togglePlay = (chapterId: string) => {
    const audio = audioRefs.current[chapterId];
    if (!audio) return;
    if (playingId === chapterId) {
      audio.pause();
    } else {
      if (playingId) audioRefs.current[playingId]?.pause();
      audio.play();
    }
  };

  /* --------------------------------- Loading -------------------------------- */

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={34} />
      </div>
    );
  }

  /* ------------------------------- Detail view ------------------------------ */

  if (selectedId) {
    if (detailLoading || !detail) {
      return (
        <div className="flex items-center justify-center h-64">
          <Spinner size={34} />
        </div>
      );
    }

    const chapters = detail.chapters;
    const completedCount = chapters.filter((ch) => ch.status === 'completed').length;
    const allCompleted = chapters.length > 0 && completedCount === chapters.length;
    const hasPending = chapters.some((ch) => ch.status === 'draft' || ch.status === 'failed');
    const isGenerating = chapters.some((ch) => ch.status === 'queued' || ch.status === 'processing');
    const progressPct = chapters.length > 0 ? Math.round((completedCount / chapters.length) * 100) : 0;

    return (
      <div className="fx max-w-[1120px] flex flex-col gap-[18px]">
        {/* Completed chapter audio elements */}
        {chapters
          .filter((ch) => ch.status === 'completed' && ch.public_url)
          .map((ch) => (
            <audio
              key={ch.id}
              ref={(el) => {
                audioRefs.current[ch.id] = el;
              }}
              src={ch.public_url!}
              onPlay={() => setPlayingId(ch.id)}
              onPause={() => setPlayingId((prev) => (prev === ch.id ? null : prev))}
              onEnded={() => setPlayingId((prev) => (prev === ch.id ? null : prev))}
              className="hidden"
            />
          ))}

        {/* Header */}
        <div className="flex items-start gap-3 flex-wrap">
          <button onClick={backToList} className="btn-ghost !py-2 !px-3.5 !text-[12.5px] flex-none">
            ← All audiobooks
          </button>
          <div className="flex-1 min-w-[200px]">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="font-display font-extrabold text-white text-[20px] m-0">{detail.name}</h2>
              <button
                type="button"
                onClick={openDetails}
                aria-label="Edit book details"
                title="Edit book details — name, author, narrator, genre, year (written into the MP3 metadata)"
                className="text-mist-500 transition-colors hover:text-white"
              >
                <Icon name="pencil" size={15} />
              </button>
              {detail.stitched_status !== 'none' && <StatusBadge status={detail.stitched_status} />}
            </div>
            {detail.description && <p className="m-0 mt-1 text-mist-400 text-[13.5px]">{detail.description}</p>}
            {(detail.author || detail.narrator) && (
              <p className="m-0 mt-1 text-mist-400 text-[12.5px]">
                {detail.author && <>by {detail.author}</>}
                {detail.author && detail.narrator && ' · '}
                {detail.narrator && <>narrated by {detail.narrator}</>}
              </p>
            )}
            <p className="m-0 mt-1 text-mist-500 text-[12.5px]">
              {chapters.length} chapter{chapters.length === 1 ? '' : 's'} · {completedCount} completed · created{' '}
              {formatDate(detail.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2.5 flex-none flex-wrap">
            <button
              onClick={generateAll}
              disabled={generateBusy || isGenerating || !hasPending}
              className="btn-grad inline-flex items-center gap-2 !py-2.5 !px-[18px] !text-xs disabled:opacity-40"
              title={
                isGenerating
                  ? 'Generation in progress'
                  : hasPending
                    ? 'Generate audio for all draft and failed chapters'
                    : 'All chapters are generated'
              }
            >
              {(generateBusy || isGenerating) && <Spinner size={13} />}
              {isGenerating ? 'Generating…' : 'Generate all'}
            </button>
            <button
              onClick={stitch}
              disabled={stitchBusy || !allCompleted || detail.stitched_status === 'stitching'}
              className="btn-accent-outline inline-flex items-center gap-2 !py-2.5 !px-[18px] !text-xs disabled:opacity-40"
              title={allCompleted ? 'Combine all chapters into one MP3' : 'All chapters must be completed first'}
            >
              {(stitchBusy || detail.stitched_status === 'stitching') && <Spinner size={13} />}
              {detail.stitched_status === 'stitching' ? 'Stitching…' : 'Stitch audiobook'}
            </button>
            <button
              onClick={downloadAllChapters}
              disabled={downloadingAll || completedCount === 0}
              className="btn-ghost inline-flex items-center gap-2 !py-2.5 !px-[18px] !text-xs disabled:opacity-40"
              title={
                completedCount > 0
                  ? `Download the ${completedCount} completed chapter${completedCount === 1 ? '' : 's'} as individual MP3 files`
                  : 'No completed chapters to download yet'
              }
            >
              {downloadingAll ? <Spinner size={13} /> : <Icon name="download" size={14} />}
              {downloadingAll ? 'Downloading…' : 'Download chapters'}
            </button>
            <button
              onClick={copyShareLink}
              disabled={shareBusy || completedCount === 0}
              className="btn-ghost inline-flex items-center gap-2 !py-2.5 !px-[18px] !text-xs disabled:opacity-40"
              title={
                completedCount > 0
                  ? 'Copy a public link anyone can use to listen to this audiobook'
                  : 'Generate at least one chapter before sharing'
              }
            >
              {shareBusy ? <Spinner size={13} /> : <Icon name={shareCopied ? 'check' : 'copy'} size={14} />}
              {shareCopied ? 'Link copied!' : detail.share_token ? 'Copy listen link' : 'Share player'}
            </button>
            {detail.share_token && (
              <button
                onClick={revokeShareLink}
                disabled={shareBusy}
                className="text-[12.5px] text-mist-500 hover:text-[#F0928C]"
                title="Disable the public listening link"
              >
                Disable link
              </button>
            )}
            {deleteProjectConfirm ? (
              <span className="flex items-center gap-2">
                <button onClick={removeProject} className="text-[12.5px] font-semibold text-[#F0928C] hover:opacity-80">
                  Confirm delete
                </button>
                <button
                  onClick={() => setDeleteProjectConfirm(false)}
                  className="text-[12.5px] text-mist-500 hover:text-mist-300"
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                onClick={() => setDeleteProjectConfirm(true)}
                aria-label="Delete audiobook"
                title="Delete audiobook"
                className="flex-none text-mist-500 transition-colors hover:text-[#F0928C]"
              >
                <Icon name="trash" size={17} />
              </button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        {chapters.length > 0 && (
          <div className="card p-[18px]">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-mist-300 text-[13px] font-semibold">
                {completedCount} / {chapters.length} chapters generated
              </span>
              <span className="font-bold text-[13px]" style={{ color: 'var(--a-hi)' }}>
                {progressPct}%
              </span>
            </div>
            <div className="h-[9px] rounded-full bg-ink-700 overflow-hidden">
              <div
                className="grad-bar h-full rounded-full transition-[width] duration-300 ease-out"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {actionError && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}>
            {actionError}
          </div>
        )}

        {/* Stitched audiobook player */}
        {detail.stitched_status === 'ready' && detail.stitched_file && (
          <div
            className="fx p-[22px] rounded-2xl"
            style={{
              border: '1px solid var(--a-core)',
              background: 'linear-gradient(180deg, rgba(var(--a-rgb), .08), rgba(23,23,27,0))',
            }}
          >
            <div className="flex items-center gap-2 mb-3.5 text-[13.5px] font-semibold" style={{ color: '#7BD0A0' }}>
              <Icon name="check" size={16} />
              Audiobook ready · {formatDuration(detail.stitched_file.duration_seconds)}
            </div>
            <audio controls src={detail.stitched_file.public_url} className="w-full" style={{ height: 40 }} />
            <div className="mt-4">
              <button
                onClick={downloadStitched}
                disabled={downloadingStitched}
                className="btn-grad inline-flex items-center gap-2 disabled:opacity-40"
              >
                {downloadingStitched ? <Spinner size={14} /> : <Icon name="download" size={15} />}
                {downloadingStitched ? 'Downloading…' : 'Download audiobook'}
              </button>
            </div>
          </div>
        )}

        {detail.stitched_status === 'failed' && (
          <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}>
            Stitching failed. Regenerate any failed chapters and try again.
          </div>
        )}

        {/* Chapter list */}
        <div className="card p-[18px] flex flex-col gap-3">
          <CardTitle>Chapters</CardTitle>
          {chapters.length === 0 ? (
            <p className="m-0 text-mist-500 text-[13.5px]">No chapters yet — add your first one below.</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {chapters.map((chapter) =>
                editingId === chapter.id ? (
                  <div key={chapter.id} className="p-4 rounded-[13px] border border-[var(--a-core)] bg-ink-950 flex flex-col gap-3">
                    <input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      maxLength={200}
                      className="input"
                      placeholder="Chapter title"
                    />
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={6}
                      className="input resize-y !text-[13.5px] leading-relaxed"
                      placeholder="Chapter text…"
                    />
                    <p className="m-0 text-mist-500 text-xs">
                      {editText.length.toLocaleString()} characters — editing the text resets the chapter to draft.
                    </p>
                    <div className="flex gap-2.5 justify-end">
                      <button onClick={() => setEditingId(null)} disabled={editBusy} className="btn-ghost !py-2 !px-4 !text-xs">
                        Cancel
                      </button>
                      <button onClick={submitEdit} disabled={editBusy} className="btn-grad !py-2 !px-4 !text-xs inline-flex items-center gap-2">
                        {editBusy && <Spinner size={12} />}
                        Save chapter
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    key={chapter.id}
                    className="flex items-center gap-3.5 px-4 py-[13px] rounded-[13px] border border-ink-800 bg-ink-900"
                  >
                    <span className="w-7 flex-none text-center font-mono text-[13px] text-mist-500">{chapter.idx}</span>
                    {chapter.status === 'completed' && chapter.public_url ? (
                      <button
                        type="button"
                        onClick={() => togglePlay(chapter.id)}
                        aria-label={playingId === chapter.id ? 'Pause chapter' : 'Play chapter'}
                        className="w-9 h-9 flex-none flex items-center justify-center rounded-[9px] border border-ink-600 bg-ink-950 cursor-pointer transition-colors hover:border-ink-500"
                      >
                        <Icon name={playingId === chapter.id ? 'pause' : 'play'} size={15} fill="var(--a-core)" />
                      </button>
                    ) : (
                      <span className="w-9 h-9 flex-none flex items-center justify-center rounded-[9px] border border-ink-800 text-mist-500">
                        {chapter.status === 'queued' || chapter.status === 'processing' ? (
                          <Spinner size={14} />
                        ) : (
                          <Icon name="book" size={15} />
                        )}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="m-0 text-white text-[14px] font-semibold truncate">{chapter.title}</p>
                      <div className="mt-[5px] h-4 overflow-hidden hidden sm:block">
                        <Waveform seed={chapter.title || chapter.id} active={playingId === chapter.id} />
                      </div>
                    </div>
                    <span className="hidden md:block flex-none font-mono text-[12.5px] text-mist-500">
                      {chapter.character_count.toLocaleString()} chars
                    </span>
                    {chapter.status === 'completed' && (
                      <span className="hidden md:block flex-none font-mono text-[12.5px] text-mist-500">
                        {formatDuration(chapter.duration_seconds)}
                      </span>
                    )}
                    <StatusBadge status={chapter.status} />
                    <span className="flex items-center gap-2.5 flex-none">
                      {(chapter.status === 'draft' || chapter.status === 'failed') && (
                        <button
                          type="button"
                          onClick={() => enhanceOneChapter(chapter.id)}
                          disabled={!!enhancingId || !!chapterGenBusy}
                          aria-label={`Enhance ${chapter.title}`}
                          title="Rewrite this chapter for narration — dramatic pauses, emphasis, and natural rhythm"
                          className="btn-ghost inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold disabled:opacity-40"
                        >
                          {enhancingId === chapter.id ? <Spinner size={13} /> : <Icon name="spark" size={14} />}
                          {enhancingId === chapter.id ? 'Enhancing…' : 'Enhance'}
                        </button>
                      )}
                      {(chapter.status === 'draft' || chapter.status === 'failed') && (
                        <button
                          type="button"
                          onClick={() => generateOneChapter(chapter.id)}
                          disabled={!!chapterGenBusy || !!enhancingId}
                          aria-label={`Generate ${chapter.title}`}
                          title="Generate this chapter"
                          className="btn-accent-outline inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold disabled:opacity-40"
                        >
                          {chapterGenBusy === chapter.id ? <Spinner size={13} /> : <Icon name="spark" size={14} />}
                          Generate
                        </button>
                      )}
                      {chapter.status === 'completed' &&
                        (clearAudioConfirm === chapter.id ? (
                          <span className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => clearChapterAudio(chapter.id)}
                              className="text-[12px] font-semibold text-[#F0928C] hover:opacity-80"
                            >
                              Clear audio
                            </button>
                            <button
                              type="button"
                              onClick={() => setClearAudioConfirm(null)}
                              className="text-[12px] text-mist-500 hover:text-mist-300"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setClearAudioConfirm(chapter.id)}
                            disabled={!!clearingAudioId || !!chapterGenBusy}
                            aria-label={`Clear audio for ${chapter.title}`}
                            title="Delete this chapter's audio and reset it to draft — the text is kept"
                            className="btn-ghost inline-flex items-center gap-1.5 !py-1.5 !px-3 !text-[12px] font-semibold disabled:opacity-40"
                          >
                            {clearingAudioId === chapter.id ? <Spinner size={13} /> : <Icon name="trash" size={13} />}
                            Clear audio
                          </button>
                        ))}
                      {chapter.status === 'completed' &&
                        (regenConfirm === chapter.id ? (
                          <span className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => generateOneChapter(chapter.id)}
                              className="text-[12px] font-semibold hover:opacity-80"
                              style={{ color: 'var(--a-hi)' }}
                            >
                              Regenerate
                            </button>
                            <button
                              type="button"
                              onClick={() => setRegenConfirm(null)}
                              className="text-[12px] text-mist-500 hover:text-mist-300"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setRegenConfirm(chapter.id)}
                            disabled={!!chapterGenBusy}
                            aria-label={`Regenerate ${chapter.title}`}
                            title="Regenerate this chapter (uses characters again)"
                            className="text-mist-500 transition-colors hover:text-white disabled:opacity-40"
                          >
                            {chapterGenBusy === chapter.id ? <Spinner size={14} /> : <Icon name="refresh" size={14} />}
                          </button>
                        ))}
                      <span className="flex flex-col -my-1">
                        <button
                          type="button"
                          onClick={() => moveChapter(chapter.id, -1)}
                          disabled={reordering || chapter.idx <= 1}
                          aria-label={`Move ${chapter.title} up`}
                          title="Move up"
                          className="text-mist-500 transition-colors hover:text-white disabled:opacity-25 disabled:cursor-default"
                        >
                          <Icon name="chevronUp" size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveChapter(chapter.id, 1)}
                          disabled={reordering || chapter.idx >= chapters.length}
                          aria-label={`Move ${chapter.title} down`}
                          title="Move down"
                          className="text-mist-500 transition-colors hover:text-white disabled:opacity-25 disabled:cursor-default"
                        >
                          <Icon name="chevronDown" size={13} />
                        </button>
                      </span>
                      <button
                        type="button"
                        onClick={() => startEdit(chapter)}
                        aria-label={`Edit ${chapter.title}`}
                        title="Edit chapter"
                        className="text-mist-500 transition-colors hover:text-white"
                      >
                        <Icon name="settings" size={15} />
                      </button>
                      {deleteConfirm === chapter.id ? (
                        <span className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => removeChapter(chapter.id)}
                            className="text-[12px] font-semibold text-[#F0928C] hover:opacity-80"
                          >
                            Confirm
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(null)}
                            className="text-[12px] text-mist-500 hover:text-mist-300"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(chapter.id)}
                          aria-label={`Delete ${chapter.title}`}
                          title="Delete chapter"
                          className="text-mist-500 transition-colors hover:text-[#F0928C]"
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      )}
                    </span>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Add chapter */}
        <div className="card p-[18px] flex flex-col gap-3">
          <CardTitle>Add chapter</CardTitle>
          <input
            value={chapterTitle}
            onChange={(e) => setChapterTitle(e.target.value)}
            maxLength={200}
            placeholder={`Chapter ${chapters.length + 1} title`}
            className="input"
          />
          <textarea
            value={chapterText}
            onChange={(e) => setChapterText(e.target.value)}
            rows={6}
            placeholder="Paste or type the chapter text…"
            className="input resize-y !text-[13.5px] leading-relaxed"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-mist-500 text-xs">{chapterText.length.toLocaleString()} characters</span>
            <button
              onClick={submitChapter}
              disabled={chapterBusy || !chapterTitle.trim() || !chapterText.trim()}
              className="btn-grad !py-2.5 !px-5 !text-xs inline-flex items-center gap-2 disabled:opacity-40"
            >
              {chapterBusy && <Spinner size={12} />}
              <Icon name="plus" size={14} />
              Add chapter
            </button>
          </div>
          {chapterError && (
            <p className="m-0 text-[12.5px]" style={{ color: '#F0928C' }}>
              {chapterError}
            </p>
          )}
        </div>

        {/* Book details modal — name/description + MP3 metadata */}
        {detailsOpen && createPortal(
          <div
            className="fixed inset-0 z-50 overflow-y-auto"
            style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
            onClick={() => !detailsBusy && setDetailsOpen(false)}
          >
            <div className="min-h-full flex items-center justify-center p-5">
              <div
                className="fx w-full max-w-md rounded-2xl border border-ink-700 bg-ink-850 p-6"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Book details"
              >
                <h3 className="font-display font-extrabold text-white text-xl m-0 mb-1">Book details</h3>
                <p className="text-mist-400 text-[13px] m-0 mb-5">
                  Written into every MP3's metadata (title, artist, album). Downloads always carry the latest
                  values — re-stitch to refresh the combined audiobook file.
                </p>

                <label className="label">Title</label>
                <input value={metaName} onChange={(e) => setMetaName(e.target.value)} maxLength={120} className="input mb-4" />

                <label className="label">Description (optional)</label>
                <input
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  maxLength={300}
                  className="input mb-4"
                />

                <label className="label">Author</label>
                <input
                  value={metaAuthor}
                  onChange={(e) => setMetaAuthor(e.target.value)}
                  maxLength={120}
                  placeholder="Shown as the MP3 artist"
                  className="input mb-4"
                />

                <label className="label">Narrator</label>
                <input
                  value={metaNarrator}
                  onChange={(e) => setMetaNarrator(e.target.value)}
                  maxLength={120}
                  placeholder="Defaults to the narration voice"
                  className="input mb-4"
                />

                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="label">Genre</label>
                    <input
                      value={metaGenre}
                      onChange={(e) => setMetaGenre(e.target.value)}
                      maxLength={120}
                      placeholder="Audiobook"
                      className="input mb-4"
                    />
                  </div>
                  <div className="w-28">
                    <label className="label">Year</label>
                    <input
                      value={metaYear}
                      onChange={(e) => setMetaYear(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
                      inputMode="numeric"
                      placeholder="2026"
                      className="input mb-4"
                    />
                  </div>
                </div>

                {detailsError && (
                  <p className="m-0 mb-4 text-[12.5px]" style={{ color: '#F0928C' }}>
                    {detailsError}
                  </p>
                )}

                <div className="flex gap-3 justify-end">
                  <button onClick={() => setDetailsOpen(false)} disabled={detailsBusy} className="btn-ghost">
                    Cancel
                  </button>
                  <button
                    onClick={submitDetails}
                    disabled={detailsBusy || !metaName.trim()}
                    className="btn-grad inline-flex items-center gap-2 disabled:opacity-40"
                  >
                    {detailsBusy && <Spinner size={14} />}
                    Save details
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  /* -------------------------------- List view ------------------------------- */

  return (
    <div className="fx max-w-[1120px] flex flex-col gap-[18px]">
      <div className="flex items-center gap-3">
        <p className="m-0 text-mist-400 text-[13.5px]">
          Turn long-form text into narrated audiobooks — chapter by chapter, stitched into one MP3.
        </p>
        <button
          onClick={() => {
            setCreateError('');
            setCreateOpen(true);
          }}
          className="btn-grad ml-auto flex-none inline-flex items-center gap-2 !py-2.5 !px-[18px] !text-xs"
        >
          <Icon name="plus" size={15} />
          New audiobook
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(216, 54, 47, .12)', color: '#F0928C' }}>
          {error}
        </div>
      )}

      {projects.length === 0 ? (
        <div className="rounded-2xl border border-ink-700 bg-ink-850 py-16 text-center">
          <span className="inline-flex text-mist-500">
            <Icon name="book" size={38} />
          </span>
          <h3 className="mt-4 text-white font-semibold text-[15px]">No audiobooks yet</h3>
          <p className="mt-1 text-mist-400 text-[13.5px]">Create your first audiobook project to get started.</p>
          <button
            onClick={() => {
              setCreateError('');
              setCreateOpen(true);
            }}
            className="btn-grad mt-6"
          >
            New audiobook
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((project) => {
            const chapterCount = project.chapter_count || 0;
            const completedCount = project.completed_count || 0;
            const pct = chapterCount > 0 ? Math.round((completedCount / chapterCount) * 100) : 0;
            return (
              <button
                key={project.id}
                onClick={() => openProject(project.id)}
                className="card lift p-[18px] text-left cursor-pointer flex flex-col gap-3"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="w-10 h-10 rounded-[11px] flex-none flex items-center justify-center"
                    style={{ background: 'rgba(var(--a-rgb), .12)', color: 'var(--a-hi)' }}
                  >
                    <Icon name="book" size={19} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="m-0 text-white text-[14.5px] font-bold truncate">{project.name}</p>
                    <p className="m-0 mt-0.5 text-mist-500 text-[12px]">
                      {chapterCount} chapter{chapterCount === 1 ? '' : 's'} ·{' '}
                      {(project.total_characters || 0).toLocaleString()} chars
                    </p>
                  </div>
                  {project.stitched_status !== 'none' && <StatusBadge status={project.stitched_status} />}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-mist-500 text-[11.5px]">
                      {completedCount}/{chapterCount} generated
                    </span>
                    <span className="text-[11.5px] font-bold" style={{ color: 'var(--a-hi)' }}>
                      {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-ink-700 overflow-hidden">
                    <div className="h-full rounded-full grad-bar" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <p className="m-0 text-mist-500 text-[11.5px]">Created {formatDate(project.created_at)}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Create modal */}
      {createOpen && createPortal(
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => !createBusy && setCreateOpen(false)}
        >
          <div className="min-h-full flex items-center justify-center p-5">
          <div
            className="fx w-full max-w-md rounded-2xl border border-ink-700 bg-ink-850 p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="New audiobook"
          >
            <h3 className="font-display font-extrabold text-white text-xl m-0 mb-1">New audiobook</h3>
            <p className="text-mist-400 text-[13px] m-0 mb-5">
              Name your project and pick a default narration voice. You can add chapters next.
            </p>

            <label className="label">Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={120}
              placeholder="e.g. The Midnight Library"
              className="input mb-4"
              autoFocus
            />

            <label className="label">Description (optional)</label>
            <input
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              maxLength={300}
              placeholder="A short note about this audiobook"
              className="input mb-4"
            />

            <label className="label">Narration voice</label>
            <select
              value={newVoice}
              onChange={(e) => {
                setNewVoice(e.target.value);
                // Default the book's language to the chosen voice's language
                const v = voices.find((x) => `${x.provider}:${x.id}` === e.target.value);
                if (v?.language && !newLanguage) setNewLanguage(v.language);
              }}
              className="input mb-4 cursor-pointer"
            >
              <option value="">Choose later</option>
              {(newLanguage ? voices.filter((v) => v.language === newLanguage) : voices).map((v) => (
                <option key={`${v.provider}:${v.id}`} value={`${v.provider}:${v.id}`}>
                  {v.name} · {v.language} ({v.provider})
                </option>
              ))}
            </select>

            <label className="label">Language</label>
            <select
              value={newLanguage}
              onChange={(e) => {
                const lang = e.target.value;
                setNewLanguage(lang);
                // Drop a voice pick that doesn't match the new language
                if (lang && newVoice && !voices.some((v) => `${v.provider}:${v.id}` === newVoice && v.language === lang)) {
                  setNewVoice('');
                }
              }}
              className="input mb-5 cursor-pointer"
            >
              <option value="">Not set</option>
              {[...new Set(voices.map((v) => v.language).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
            <p className="m-0 -mt-3 mb-4 text-[11.5px] text-mist-500">
              Used by chapter enhancement so pauses and emphasis follow the right language.
            </p>

            {createError && (
              <p className="m-0 mb-4 text-[12.5px]" style={{ color: '#F0928C' }}>
                {createError}
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateOpen(false)} disabled={createBusy} className="btn-ghost">
                Cancel
              </button>
              <button
                onClick={submitCreate}
                disabled={createBusy || !newName.trim()}
                className="btn-grad inline-flex items-center gap-2 disabled:opacity-40"
              >
                {createBusy && <Spinner size={14} />}
                Create audiobook
              </button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
